import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { cn } from "@/lib/utils";
import { errorMessage, useMonitoringInstances } from "../../api";
import {
  Ago,
  EmptyBlock,
  ErrorBlock,
  FetchingDot,
  LevelBadge,
  LoadingBlock,
  SectionCard,
  SeverityIcon,
  StatePill,
} from "../../components/primitives";
import { formatCount } from "../../format";
import { useMonitoring } from "../../monitoring-context";
import type { EventLevel, InstanceInfo, RuntimeSection } from "../../types";
import { FactList, SubHeading } from "./stat-tile";
import { LIMITS, type Tone } from "./tone";

const LEVELS: EventLevel[] = ["error", "warning", "info"];
const KNOWN_INTEGRATIONS = ["biteberry.order", "biteberry.item", "biteberry.status", "autoscaler"];

/** Whether the board's own data is saved: persistence, flush ages and errors, dropped / recorded events. */
export function MonitoringHealthCard({ runtime, updatedAt }: { runtime: RuntimeSection; updatedAt: number }) {
  const { t } = useLocalization();
  const status = runtime.monitoring;
  const configured = (runtime.settings?.monitoring as { persist?: boolean } | undefined)?.persist;
  const persistTone: Tone = !status.persist && configured === true ? "warn" : "neutral";

  const flush = (age: number | null): { value: JSX.Element; tone?: Tone } => {
    if (!status.persist) return { value: <span className="text-muted-foreground">–</span> };
    if (age === null) return { value: <>{t("monitoring.backend.mon.never")}</>, tone: "warn" };
    return {
      value: <Ago ts={updatedAt - age * 1000} />,
      tone: age > LIMITS.flushStaleS ? "warn" : undefined,
    };
  };
  const metricsFlush = flush(status.metrics_last_flush_age_s);
  const eventsFlush = flush(status.events_last_flush_age_s);
  const errors = [
    { label: t("monitoring.backend.mon.metricsError"), error: status.metrics_last_error },
    { label: t("monitoring.backend.mon.eventsError"), error: status.events_last_error },
  ].filter((item) => item.error);

  return (
    <SectionCard title={t("monitoring.backend.mon.title")} description={t("monitoring.backend.mon.description")}>
      <div className="flex flex-col gap-4">
        <FactList
          min="15rem"
          items={[
            {
              label: t("monitoring.backend.mon.persist"),
              tone: persistTone,
              value: status.persist
                ? t("monitoring.backend.mon.persistOn")
                : configured === true
                  ? t("monitoring.backend.mon.persistFailed")
                  : t("monitoring.backend.mon.persistOff"),
            },
            { label: t("monitoring.backend.mon.metricsFlush"), value: metricsFlush.value, tone: metricsFlush.tone },
            { label: t("monitoring.backend.mon.eventsFlush"), value: eventsFlush.value, tone: eventsFlush.tone },
            {
              label: t("monitoring.backend.mon.dropped"),
              value: formatCount(status.events_dropped),
              tone: status.events_dropped > 0 ? "warn" : undefined,
              hint: t("monitoring.backend.mon.droppedHint"),
            },
            { label: t("monitoring.backend.mon.monitoredTasks"), value: formatCount(runtime.monitored_tasks) },
          ]}
        />
        {errors.map((item) => (
          <div key={item.label} className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            <div className="mb-1 flex items-center gap-2 font-medium">
              <SeverityIcon severity="critical" className="h-3.5 w-3.5" />
              {item.label}
            </div>
            <p className="break-words font-mono text-[11px] leading-snug">{item.error}</p>
          </div>
        ))}
        <div className="flex flex-col gap-2">
          <SubHeading>{t("monitoring.backend.mon.recorded")}</SubHeading>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {LEVELS.map((level) => (
              <span key={level} className="inline-flex items-center gap-2">
                <LevelBadge level={level} />
                <span className="font-mono text-xs tabular-nums">{formatCount(status.events_recorded?.[level] ?? 0)}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

/** Last calls of the external integrations received by this instance since it started. */
export function IntegrationsCard({ integrations }: { integrations: RuntimeSection["integrations"] }) {
  const { t, tp } = useLocalization();
  const names = [...KNOWN_INTEGRATIONS, ...Object.keys(integrations ?? {}).filter((name) => !KNOWN_INTEGRATIONS.includes(name)).sort()];
  return (
    <SectionCard title={t("monitoring.backend.int.title")} description={t("monitoring.backend.int.description")}>
      <div className="flex flex-col divide-y divide-border/60">
        {names.map((name) => {
          const entry = integrations?.[name];
          return (
            <div key={name} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2">
              <div className="min-w-0 flex-1">
                <span className="text-sm">
                  {KNOWN_INTEGRATIONS.includes(name) ? t(`monitoring.backend.int.${name}` as TranslationKey) : name}
                </span>
                <span className="ml-2 break-all font-mono text-[11px] text-muted-foreground">{name}</span>
              </div>
              {entry ? (
                <span className="flex flex-wrap items-baseline gap-x-2 text-xs tabular-nums">
                  <span className="text-muted-foreground">{tp("monitoring.backend.int.calls", entry.count, { count: formatCount(entry.count) })}</span>
                  <Ago ts={entry.last_at} />
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">{t("monitoring.backend.int.never")}</span>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

/** Backend processes that wrote metrics in the last 24 h; each can scope the whole board. */
export function InstancesCard() {
  const { t } = useLocalization();
  const query = useMonitoringInstances();
  const { scope, setScope } = useMonitoring();
  const instances = query.data?.instances ?? [];

  const action = (instance: InstanceInfo) =>
    scope.instance === instance.instance ? (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <StatePill tone="neutral">{t("monitoring.backend.inst.scoped")}</StatePill>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => setScope({ instance: null, all: false })}
        >
          <X className="h-3.5 w-3.5" />
          {t("monitoring.backend.inst.clear")}
        </Button>
      </span>
    ) : (
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        onClick={() => setScope({ instance: instance.instance, all: false })}
      >
        <Filter className="h-3.5 w-3.5" />
        {t("monitoring.backend.inst.scope")}
      </Button>
    );

  const name = (instance: InstanceInfo) => (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <span className="break-all font-mono">{instance.instance}</span>
      {instance.current && (
        <span className="rounded border border-border px-1.5 py-px text-[10px] text-muted-foreground">
          {t("monitoring.scope.current")}
        </span>
      )}
    </span>
  );

  return (
    <SectionCard
      title={t("monitoring.backend.inst.title")}
      description={t("monitoring.backend.inst.description")}
      actions={<FetchingDot active={query.isFetching} />}
    >
      {query.isPending ? (
        <LoadingBlock height={120} />
      ) : query.isError && !query.data ? (
        <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      ) : instances.length === 0 ? (
        <EmptyBlock>{t("monitoring.backend.inst.empty")}</EmptyBlock>
      ) : (
        <>
          <div className="hidden md:block">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-8">{t("monitoring.backend.inst.col.instance")}</TableHead>
                  <TableHead className="h-8">{t("monitoring.backend.inst.col.role")}</TableHead>
                  <TableHead className="h-8">{t("monitoring.backend.inst.col.first")}</TableHead>
                  <TableHead className="h-8">{t("monitoring.backend.inst.col.last")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.backend.inst.col.requests")}</TableHead>
                  <TableHead className="h-8 text-right">
                    <span className="sr-only">{t("monitoring.backend.inst.col.action")}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instances.map((instance) => (
                  <TableRow key={instance.instance} className={cn(scope.instance === instance.instance && "bg-muted/40")}>
                    <TableCell className="max-w-[18rem] py-2">{name(instance)}</TableCell>
                    <TableCell className="py-2">{instance.role ?? "–"}</TableCell>
                    <TableCell className="py-2">
                      <Ago ts={instance.first} />
                    </TableCell>
                    <TableCell className="py-2">
                      <Ago ts={instance.last} />
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono tabular-nums">{formatCount(instance.requests)}</TableCell>
                    <TableCell className="py-2 text-right">{action(instance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col gap-2 md:hidden">
            {instances.map((instance) => (
              <div
                key={instance.instance}
                className={cn("flex flex-col gap-2 rounded-md border px-3 py-2 text-xs", scope.instance === instance.instance && "bg-muted/40")}
              >
                {name(instance)}
                <FactList
                  min="100%"
                  items={[
                    { label: t("monitoring.backend.inst.col.role"), value: instance.role ?? "–" },
                    { label: t("monitoring.backend.inst.col.first"), value: <Ago ts={instance.first} /> },
                    { label: t("monitoring.backend.inst.col.last"), value: <Ago ts={instance.last} /> },
                    { label: t("monitoring.backend.inst.col.requests"), value: formatCount(instance.requests) },
                  ]}
                />
                <div>{action(instance)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
}
